import React, { createContext, useContext, useState } from 'react';
import { ResponsiveTableColumn, ResponsiveTableSortProps } from './contracts';

interface ResponsiveTableContextValue<T> {
	data: T[];
	columns: ResponsiveTableColumn<T>[];
	selectedRows: string[];
	addRowSelection: (row: string) => void;
	removeRowSelection: (row: string) => void;
	/**Select all the rows on the page */
	selectAllRows: () => void;
	/**Clear out all selections */
	clearRowSelection: () => void;
	rowIdentifier?: string;
	rowShowSelectorEvaluatorFunction?: (data: T) => boolean;
	rowActionComponent?: (data: T) => React.ReactElement;
	mobileRowActionComponent?: (data: T) => React.ReactElement;
	selectedRowActionComponent?: (data: T[], selectedRows: string[]) => React.ReactElement;
	sortProps?: ResponsiveTableSortProps;
	loading?: boolean;
	rowsExpandedOnSmallScreens?: boolean;
	disableTooltips?: boolean;
}

const ResponsiveTableContext = createContext<ResponsiveTableContextValue<any>>(undefined as any); //hmm not sure how to sort this with T

type ResponsiveTableContextProviderProps<T> = {
	data: T[];
	columns: ResponsiveTableColumn<T>[];
	children: React.ReactNode;
	/**Needed for row selector in order to identify a record, should be unique (idealy an id)  */
	rowIdentifier?: string;
	rowActionComponent?: (data: T) => React.ReactElement;
	/**If you only want specific rows to have a selector, use this function to determine if we show the checkbox for each row based on the record input */
	rowShowSelectorEvaluatorFunction?: (data: T) => boolean;
	mobileRowActionComponent?: (data: T) => React.ReactElement;
	selectedRowActionComponent?: (data: T[], selectedRows: string[]) => React.ReactElement;
	sortProps?: ResponsiveTableSortProps;
	loading?: boolean;
	rowsExpandedOnSmallScreens?: boolean;
	disableTooltips?: boolean;
};

export function ResponsiveTableContextProvider<T>({
	data,
	columns,
	children,
	sortProps,
	rowActionComponent,
	mobileRowActionComponent,
	selectedRowActionComponent,
	loading,
	rowIdentifier,
	rowShowSelectorEvaluatorFunction,
	rowsExpandedOnSmallScreens,
	disableTooltips
}: ResponsiveTableContextProviderProps<T>) {
	const [selectedRows, setSelectedRows] = useState<string[]>([]);

	function addRowSelection(rowId: string) {
		setSelectedRows(prev => [...prev, rowId]);
	}
	function removeRowSelection(rowId: string) {
		setSelectedRows(prev => prev.filter(s => s !== rowId));
	}
	function selectAllRows() {
		if (!rowIdentifier) return;
		setSelectedRows(data.filter(d => rowShowSelectorEvaluatorFunction?.(d) ?? true).map(d => d[rowIdentifier]));
	}
	function clearRowSelection() {
		setSelectedRows([]);
	}

	return (
		<ResponsiveTableContext.Provider
			value={{
				data,
				columns,
				sortProps,
				rowActionComponent,
				mobileRowActionComponent,
				selectedRowActionComponent,
				loading,
				addRowSelection,
				clearRowSelection,
				removeRowSelection,
				selectAllRows,
				selectedRows,
				rowIdentifier,
				rowShowSelectorEvaluatorFunction,
				rowsExpandedOnSmallScreens,
				disableTooltips
			}}
		>
			{children}
		</ResponsiveTableContext.Provider>
	);
}

export function useResponsiveTableContext() {
	return useContext(ResponsiveTableContext);
}
